import React, { useRef, useState } from "react";
import { useApplication } from "./ApplicationProvider";
import { usePublicConfig } from "./PublicConfigProvider";

export function FileUpload({
  fieldKey,
  label,
  helper,
  accept = ".pdf,.jpg,.jpeg,.png",
  required,
}: {
  fieldKey: string;
  label: string;
  helper?: string;
  accept?: string;
  required?: boolean;
}) {
  const { data, setUpload, ensureDraft } = useApplication();
  const cfg = usePublicConfig();
  const inputRef = useRef<HTMLInputElement | null>(null);
  const [error, setError] = useState<string>("");
  const [uploading, setUploading] = useState<boolean>(false);
  const current = data.uploads[fieldKey];

  // Files are stored on SharePoint under the draft, and the draft is keyed by the
  // student + parent/guardian emails. Those emails now live on Step 1 — Basic
  // Information; read them from the SAME source the Step 1 gate and the draft
  // create use (data.step2.studentEmail / parentEmail) so uploads unlock the
  // instant both Step 1 emails are present. (Round 16: the fields moved pages but
  // not their storage location.)
  const emailsReady =
    /.+@.+\..+/.test(data.step2.studentEmail) && /.+@.+\..+/.test(data.step2.parentEmail);

  const handleFile = async (file: File) => {
    setError("");
    if (file.size > cfg.maxUploadMb * 1024 * 1024) {
      setError(`File is too large. Maximum size: ${cfg.maxUploadMb} MB.`);
      return;
    }
    // Only the genuine "emails not yet entered" case shows the Step 1 prompt.
    if (!emailsReady) {
      setError(
        "Enter the student and parent/guardian emails on Step 1 — Basic Information first, then upload."
      );
      return;
    }
    setUploading(true);
    try {
      // Emails are present, so ensureDraft creates the server draft on demand if
      // one doesn't exist yet (the create reads the same Step 1 emails). A null
      // here is a draft/server problem, NOT a missing-email problem.
      const token = await ensureDraft();
      if (!token) {
        setError(
          "We couldn't prepare your application draft to attach this file. Your work is saved on this device — please try again in a moment."
        );
        return;
      }
      const form = new FormData();
      form.append("file", file);
      const res = await fetch(
        `/api/drafts/${encodeURIComponent(token)}/uploads/${encodeURIComponent(fieldKey)}`,
        { method: "POST", body: form }
      );
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      const ref = await res.json();
      setUpload(fieldKey, ref);
    } catch {
      setError("Upload failed. Please check your connection and try again.");
    } finally {
      setUploading(false);
    }
  };

  return (
    <div className="rounded-md border border-slate-200 bg-slate-50 p-3">
      <div className="flex flex-wrap items-start gap-3 justify-between">
        <div className="min-w-0">
          <p className="font-medium text-sm text-slate-800">
            {label} {required && <span className="text-red-600">*</span>}
          </p>
          {helper && <p className="text-xs text-slate-600 mt-1">{helper}</p>}
        </div>
        <div className="flex items-center gap-2">
          {current ? (
            <span className="text-xs text-green-700 flex items-center gap-1">
              ✓ Uploaded: <span className="truncate max-w-[160px]">{current.filename}</span>
            </span>
          ) : (
            <span className="text-xs text-slate-500">Not yet uploaded</span>
          )}
          <button
            type="button"
            onClick={() => inputRef.current?.click()}
            disabled={uploading}
            className="btn-secondary text-xs"
          >
            {uploading ? "Uploading…" : current ? "Replace" : "Upload"}
          </button>
        </div>
      </div>
      <input
        ref={inputRef}
        type="file"
        accept={accept}
        className="hidden"
        onChange={(e) => {
          const f = e.target.files?.[0];
          if (f) void handleFile(f);
          e.target.value = "";
        }}
      />
      {error && <p className="field-error">{error}</p>}
    </div>
  );
}
